/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import {ErrorType, IndexedObject, ResponseData, showDialog, showSnackbar} from '@dvrd/dvr-controls';
import React, {ForwardedRef, forwardRef, MouseEventHandler, useContext, useImperativeHandle, useState} from 'react';
import Invoice from "../../../models/invoiceModel";
import {ActivateHandle, UnlayerDesign} from "../../../util/interfaces";
import {createErrorMessage} from "../../../util/utils";
import SendInvoice from "./sendInvoice";
import {AppContext} from "../../misc/appContext";

interface Props {
    onSuccess: (invoice: Invoice) => void;
    onDownloadInvoice: (invoice: Invoice) => MouseEventHandler;
    onAssignInvoiceNumber: (invoice: Invoice) => MouseEventHandler;
}

function SendInvoiceController(props: Props, ref: ForwardedRef<ActivateHandle>) {
    const context = useContext(AppContext);
    const {onSuccess, onDownloadInvoice, onAssignInvoiceNumber} = props;
    const [active, setActive] = useState(false);
    const [loading, setLoading] = useState(false);
    const [invoice, setInvoice] = useState<Invoice | null>(null);
    const [subject, setSubject] = useState('');
    const [error, setError] = useState<ErrorType>(null);

    function onActivate(invoice: Invoice) {
        setInvoice(invoice);
        setActive(true);
        setSubject(`Factuur voor bestelling ${invoice.orderId}`);
    }

    function onClose() {
        setActive(false);
        setLoading(false);
        setInvoice(null);
        setSubject('');
    }

    function onChangeSubject(value: string) {
        setSubject(value);
        setError(null);
    }

    function onSubmit(data: { design: UnlayerDesign, html: string }) {
        if (!validateValues(data.html)) return;
        if (!invoice) return;
        setLoading(true);
        invoice.sendConfigured(data.html, data.design, subject, (invoice: Invoice, success: boolean, data: ResponseData) => {
            setLoading(false);
            if (success) {
                saveToContext(data.design);
                onSuccess(invoice);
                onClose();
                showSnackbar('De factuur is verzonden');
            } else onSendingFailed(data);
        });
    }

    function onSendingFailed(data: IndexedObject<any>) {
        const {error_type, message, order_id} = data;
        if (error_type === 'email_not_available') {
            showDialog(<label>Het originele e-mailadres van de klant is niet meer beschikbaar voor Bolmate. We raden
                aan om de factuur te <span className='invoice-download-link'
                                           onClick={_onDownloadInvoice}>downloaden</span> en
                deze via het Bol-platform te verzenden naar de klant.</label>, 'Verzenden mislukt');
        } else if (error_type === 'invoice_num_not_set') {
            if (invoice)
                showDialog('Deze factuur heeft nog geen factuurnummer en kan daarom niet verzonden worden. Het systeem ' +
                    'kan geen factuurnummer bepalen voor orders die ouder zijn dan jouw Bolmate account. Voor deze orders ' +
                    'dien je dus zelf het factuurnummer in te stellen.', 'Factuurnummer instellen',
                    [{label: 'Factuurnummer instellen', onClick: _onAssignInvoiceNumber},
                        {label: 'Sluiten', primary: !invoice}]);
            else
                showDialog(`Order ${order_id} heeft nog geen factuurnummer en kan daarom niet verzonden worden. Het systeem 
                    kan geen factuurnummer bepalen voor orders die ouder zijn dan jouw Bolmate account. Voor deze orders 
                    dien je dus zelf het factuurnummer in te stellen.`, 'Factuurnummer instellen')
        } else
            showDialog(createErrorMessage(message ?? 'Het verzenden van de factuur is niet gelukt.'), 'Verzenden mislukt');
    }

    function _onDownloadInvoice() {
        onDownloadInvoice(invoice!);
        onClose();
    }

    function _onAssignInvoiceNumber(evt: React.MouseEvent) {
        onAssignInvoiceNumber(invoice!)(evt);
        onClose();
    }

    function saveToContext(design: UnlayerDesign) {
        const {account} = context.accountContext;
        if (!account) return;
        const settings = account.settings?.clone();
        if (!settings) return;
        settings.manualMailJson = design;
        account.settings = settings;
        context.onChangeContext({account});
    }

    function validateValues(mailText: string): boolean {
        if (!subject) {
            setError('Vul het onderwerp in');
            return false;
        } else if (!mailText) {
            setError('Vul de tekst van de e-mail in');
            return false;
        }
        return true;
    }

    useImperativeHandle(ref, () => ({
        activate: onActivate,
    }));

    return (
        <SendInvoice onClose={onClose} onSubmit={onSubmit} onChangeSubject={onChangeSubject} active={active}
                     subject={subject} loading={loading} invoice={invoice} error={error}/>
    )
}

export default forwardRef<ActivateHandle, Props>(SendInvoiceController);